import axios from 'axios';
import { configService } from '../services/configService';

/** Slug fiche annuaire à rattacher à la création du profil (?claim=). */

export const DIRECTORY_CLAIM_SLUG_KEY = 'directory_claim_slug';

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function normalizeDirectoryClaimSlug(
  value: string | null | undefined
): string | null {
  if (typeof value !== 'string') return null;
  const slug = value.trim().toLowerCase();
  if (!slug || slug.length > 255 || !SLUG_RE.test(slug)) return null;
  return slug;
}

export function persistDirectoryClaimSlug(slug: string | null | undefined): void {
  const normalized = normalizeDirectoryClaimSlug(slug);
  if (!normalized) return;
  try {
    sessionStorage.setItem(DIRECTORY_CLAIM_SLUG_KEY, normalized);
  } catch {
    /* ignore quota */
  }
}

/** Query prioritaire, sinon le slug mémorisé dans l’onglet. */
export function readDirectoryClaimSlug(
  queryValue?: string | null
): string | undefined {
  const fromQuery = normalizeDirectoryClaimSlug(queryValue);
  if (fromQuery) {
    persistDirectoryClaimSlug(fromQuery);
    return fromQuery;
  }
  try {
    return (
      normalizeDirectoryClaimSlug(sessionStorage.getItem(DIRECTORY_CLAIM_SLUG_KEY)) ||
      undefined
    );
  } catch {
    return undefined;
  }
}

export function clearDirectoryClaimSlug(): void {
  try {
    sessionStorage.removeItem(DIRECTORY_CLAIM_SLUG_KEY);
  } catch {
    /* ignore */
  }
}

export function directoryClaimPayload(
  queryValue?: string | null
): { claim_slug?: string } {
  const slug = readDirectoryClaimSlug(queryValue);
  return slug ? { claim_slug: slug } : {};
}

/** Ajoute ?claim= au chemin d’inscription si un slug est en cours. */
export function appendDirectoryClaim(path: string): string {
  const slug = readDirectoryClaimSlug();
  if (!slug) return path;
  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const qIndex = withoutHash.indexOf('?');
  const pathname = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash;
  const params = new URLSearchParams(qIndex >= 0 ? withoutHash.slice(qIndex + 1) : '');
  params.set('claim', slug);
  return `${pathname}?${params.toString()}${hash}`;
}

export function directoryClaimToastKind(
  status: unknown
): 'claimed' | 'proof_sent' | null {
  if (status === 'claimed') return 'claimed';
  if (status === 'proof_sent') return 'proof_sent';
  return null;
}

export type DirectoryClaimPoster = (
  url: string,
  token: string
) => Promise<{ status?: unknown }>;

async function postDirectoryClaim(
  url: string,
  token: string
): Promise<{ status?: unknown }> {
  const response = await axios.post(
    url,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data ?? {};
}

function claimHttpStatus(error: unknown): number | undefined {
  if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(error)) {
    return error.response?.status;
  }
  if (error && typeof error === 'object' && 'httpStatus' in error) {
    const status = Number((error as { httpStatus: unknown }).httpStatus);
    return Number.isFinite(status) ? status : undefined;
  }
  return undefined;
}

/**
 * Pro déjà inscrit : POST /booking-directory/{slug}/claim.
 * 400/404 oublient le slug. Une panne réseau le garde pour le prochain passage.
 */
export async function claimPendingDirectoryListing(
  token: string,
  post: DirectoryClaimPoster = postDirectoryClaim
): Promise<'claimed' | 'proof_sent' | null> {
  const slug = readDirectoryClaimSlug();
  if (!slug || !token) return null;
  const url = configService.getFullApiUrl(
    `/booking-directory/${encodeURIComponent(slug)}/claim`
  );
  try {
    const data = await post(url, token);
    const kind = directoryClaimToastKind(data?.status);
    if (kind) clearDirectoryClaimSlug();
    return kind;
  } catch (error) {
    const status = claimHttpStatus(error);
    if (status === 400 || status === 404) clearDirectoryClaimSlug();
    return null;
  }
}
